<template>
    <div>

        <PageHeader>
            {{ $t('competitiveAnalysis') }}
            <template #side>
                <div class="d-flex gap-3">
                    <TimeRangeCalendar v-model="timeRange" />
                </div>
            </template>
        </PageHeader>

        <Card :header="$t('selectedProducts')">
            <template #nav>
                <div :key="spaceId" class="d-flex gap-3">
                    <MultiselectAvailableCampaigns :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                    <ButtonIcon variant="primary" :disabled="loading" :loading="false" @click="createSet()">
                        {{ $t('createSet') }}
                    </ButtonIcon>
                </div>
            </template>
            <RankingTable
                :items="items"
                :busy-items="busyItems"
                :loading="loading"
                :error="error"
                :total-rows="dataCompetitionReportSet?.max"
                :current-page.sync="tableParams.page"
                :per-page.sync="tableParams.perPage"
                selectable-id-key="reportId"
                no-sort-reset
                no-local-sorting
            />
        </Card>

        <CurrentItem v-if="currentItemToShow" />

        <ModalForm />

    </div>
</template>



<script>
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import CurrentItem from './parts/CurrentItem.vue';
    import RankingTable from './parts/RankingTable.vue';
    import ModalForm from './parts/ModalForm.vue';

    /** Czytelny klucz pozycji dla rankingów */
    const getUniqueKey = product => `${product.id}#${product.type}`;

    /** Numer dnia UTC (do grupowania ciągłych dat) */
    const toUtcDay = dateString => {
        const date = new Date(`${dateString}T00:00:00Z`);
        return Math.floor(date.getTime() / 86400000);
    };

    /** Datę YYYY-MM-DD na ms UTC (do porównań w seriach cen) */
    const dateStringToUtcMs = dateString => {
        const date = new Date(`${dateString}T00:00:00Z`);
        return date.getTime();
    };

    /** Projekcja produktu do widoku */
    const getProductView = (product, type) => ({
        id: product.id,
        name: product.name,
        sku: product.sku,
        ean: product.ean,
        type,
    });

    export default {
        name: 'CompetitiveAnalysis',
        components: {
            TimeRangeCalendar,
            MultiselectAvailableCampaigns,
            RankingTable,
            CurrentItem,
            ModalForm,
        },
        provide() {
            return { parentComponent: this };
        },
        data() {
            return {
                requestSpacesCompetitionReportSet: this.$req('CompetitionReport/SpacesCompetitionReportSet'),
                requestSpacesCompetitionReport: this.$req('CompetitionReport/SpacesCompetitionReport'),
                requestCompetitionReport: this.$req('CompetitionReport/CompetitionReport'),
                requestCompetitionReportSet: this.$req('CompetitionReport/CompetitionReportSet'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                timeRange: null,
                campaigns: [],
                products: [],
                aggregations: [],
                currentItemToShow: null,
                campaignsLoading: false,
                busyItems: [],
                tableParams: {
                    perPage: 10,
                    page: 1,
                },
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            loadingFilters() {
                return this.campaignsLoading || this.requestSpacesCompetitionReportSet.getLoading();
            },
            loading() {
                return this.requestSpacesCompetitionReport.getLoading() || this.loadingFilters;
            },
            dataCompetitionReportSet() {
                return this.requestSpacesCompetitionReportSet.getData();
            },
            /** koniec zakresu w ms (lokalny czas przeglądarki) */
            endMs() {
                const endDate = this.timeRange?.endDate;
                return endDate ? new Date(endDate).getTime() : null;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = { startDate, endDate };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (this.products?.length) requestParams.products = this.products.join(',');
                if (this.aggregations?.length) requestParams.aggregations = this.aggregations.join(',');
                return { urlParams, requestParams };
            },
            apiParamsKey() {
                return JSON.stringify(this.apiParams || {});
            },
            error() {
                return this.requestSpacesCompetitionReportSet.getError() || this.requestSpacesCompetitionReport.getError();
            },
            /** Zbiór zestawów produktów: main + powiązane */
            productSet() {
                const groupItems = this.dataCompetitionReportSet?.items || [];
                const result = [];
                for (const groupItem of groupItems) {
                    const main = groupItem.producerProduct;
                    if (!main) continue;

                    const mainProduct = getProductView(main, 'main');
                    const producerProducts = (groupItem.producerProducts || []).map(entry => getProductView(entry, 'producer'));
                    const globalProducts = (groupItem.globalProducts || []).map(entry => getProductView(entry, 'global'));

                    result.push({
                        reportId: groupItem.id, // <<<<<< potrzebne do PATCH/DELETE
                        producerProduct: mainProduct,
                        producerProducts,
                        globalProducts,
                        // dla wygody – pełna lista do obliczeń:
                        allProducts: [mainProduct, ...producerProducts, ...globalProducts],
                    });
                }
                return result;
            },
            hasProductSet() {
                return this.productSet.length > 0;
            },
            items() {
                // if (this.loading || !this.hasProductSet) return [];
                // budujemy item “do tabeli” + *doklejamy* info o zestawie
                return this.productSet
                    .map(setObj => {
                        const tableItem = this.getDataForSet(setObj.allProducts, setObj.producerProduct.id);
                        if (!tableItem) return null;
                        return {
                            ...tableItem,
                            // >>> DODATKOWE pola potrzebne do edycji:
                            reportId: setObj.reportId,
                            producerProduct: setObj.producerProduct,
                            producerProducts: setObj.producerProducts,
                            globalProducts: setObj.globalProducts,
                        };
                    })
                    .filter(Boolean);
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
                this.fetchReportSet();
            },
            apiParamsKey() {
                this.loadData();
            },
            loadingFilters() {
                this.loadData();
            },
            tableParams: {
                deep: true,
                handler() {
                    this.fetchReportSet();
                },
            },
            items() {
                this.reloadCurrentItem();
            },
        },
        methods: {
            loadData() {
                if (this.loadingFilters) return;
                this.resetData();
                this.fetchData();
            },
            resetData() {
                this.requestSpacesCompetitionReport.abortAll();
                this.requestSpacesCompetitionReport.resetAll();
            },
            resetFilters() {
                this.campaigns = [];
                this.products = [];
                this.requestSpacesCompetitionReportSet.abortAll();
                this.requestSpacesCompetitionReportSet.resetAll();
            },
            fetchData() {
                // if (!this.products?.length || !this.aggregations?.length) {
                //     this.fetchReportSet();
                // } else {
                //     this.fetchReport();
                // }
                this.fetchReport();
            },
            reloadCurrentItem() {
                if (this.currentItemToShow) {
                    const currentItemToShow = this.items.find(item => item.reportId === this.currentItemToShow.reportId);
                    this.currentItemToShow = currentItemToShow || null;
                }
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchReportSet() {
                const params = { requestParams: { ...this.tableParams } };
                return this.requestSpacesCompetitionReportSet
                    .get(params)
                    .then(this.initParams)
                    .catch(() => {});
            },
            fetchReport() {
                return this.requestSpacesCompetitionReport.get(this.apiParams).catch(() => {});
            },
            /** Inicjalizacja filtrów produktowych/aglomeracji na podstawie setu */
            initParams(reportSetData) {
                const setItems = reportSetData?.items || [];
                const productIds = new Set();
                const aggregationIds = new Set();

                for (const groupItem of setItems) {
                    const mainId = groupItem?.producerProduct?.id;
                    if (mainId != null) productIds.add(mainId);

                    for (const productEntry of groupItem?.producerProducts || []) {
                        if (productEntry?.id != null) productIds.add(productEntry.id);
                    }
                    for (const globalEntry of groupItem?.globalProducts || []) {
                        if (globalEntry?.id != null) aggregationIds.add(globalEntry.id);
                    }
                }

                if (productIds.size) this.products = [...productIds];
                if (aggregationIds.size) this.aggregations = [...aggregationIds];
            },
            hideItem() {
                this.currentItemToShow = null;
            },
            showItem(item) {
                this.currentItemToShow = item;
            },
            editItem(item) {
                this.createSet(item);
            },
            createSet(item = null) {
                this.$root.$emit('bv::show::modal', 'modal-form-competitive-analysis');
                this.$root.$emit('modal-form-competitive-analysis', item);
            },
            deleteItem(item) {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmYouWantDelete'), {
                        title: this.$t('confirm'),
                        okTitle: this.$t('yes'),
                        cancelTitle: this.$t('cancel'),
                        okVariant: 'danger',
                    })
                    .then(value => {
                        if (value) {
                            if (item === this.currentItemToShow) {
                                this.hideItem();
                            }
                            const id = item.reportId;
                            const set = new Set(this.busyItems);
                            set.add(id);
                            this.busyItems = Array.from(set);
                            this.requestCompetitionReportSet
                                .id(`delete-${id}`)
                                .delete({ urlParams: { id } })
                                .finally(() => {
                                    set.delete(id);
                                    this.busyItems = Array.from(set);
                                    this.fetchReportSet();
                                });
                        }
                    });
            },
            /** Zbiera i scala dane dla danego zestawu produktów (main + powiązane) */
            getDataForSet(productList, targetProductId, targetProductType) {
                const reportData = this.requestSpacesCompetitionReport.getData();

                const mappedProducts = productList.map(productMeta => {
                    const productData = productMeta.type === 'global' ? reportData?.aggregations?.[productMeta.id] : reportData?.products?.[productMeta.id];

                    const { avgPrice, minPrice, maxPrice, priceRangeMin, priceRangeMax } = this.calculatePriceData(productData);

                    const offersNumber = this.calculateOffersNumber(productData);

                    /** Wystąpienia min/max cen — klucze już są nazwami (z price[]), nie ID */
                    const minPriceOccurrences = {};
                    const maxPriceOccurrences = {};

                    if (productData?.price) {
                        const minDatesByShop = {};
                        const maxDatesByShop = {};
                        productData.price.forEach(pricesForDay => {
                            const dateString = pricesForDay.date;
                            for (const [shopLabel, priceValue] of Object.entries(pricesForDay)) {
                                if (shopLabel === 'date' || priceValue == null) continue;
                                if (priceValue === minPrice) (minDatesByShop[shopLabel] ??= []).push(dateString);
                                if (priceValue === maxPrice) (maxDatesByShop[shopLabel] ??= []).push(dateString);
                            }
                        });
                        for (const [shopLabel, dates] of Object.entries(minDatesByShop)) {
                            minPriceOccurrences[shopLabel] = this.groupDatesToMsRanges(dates);
                        }
                        for (const [shopLabel, dates] of Object.entries(maxDatesByShop)) {
                            maxPriceOccurrences[shopLabel] = this.groupDatesToMsRanges(dates);
                        }
                    }

                    /** Dostępność: kluczami ZAWSZE nazwy sklepów (offer.campaign lub z serii cen) */
                    const availabilityRanges = this.buildAvailabilityRanges(productData);
                    const allPriceOccurrences = availabilityRanges.perShop;
                    const allPriceOccurrencesAccumulated = availabilityRanges.accumulated;

                    /** Oferty: metadane wyłącznie z offer.* */
                    const { allOffers, activeOffers } = this.buildOffersForProduct(productData, this.endMs);

                    return {
                        ...productMeta,
                        avgPrice,
                        minPrice,
                        maxPrice,
                        priceRangeMin,
                        priceRangeMax,
                        offersNumber,
                        minPriceOccurrences,
                        maxPriceOccurrences,
                        allPriceOccurrences,
                        allPriceOccurrencesAccumulated,
                        allOffers,
                        activeOffers,
                    };
                });

                /** Rankingi */
                const rankingByAvgPriceMap = this.createRankingMap(mappedProducts, (left, right) => (left.avgPrice ?? Infinity) - (right.avgPrice ?? Infinity));
                const rankingByOffersMap = this.createRankingMap(mappedProducts, (left, right) => right.offersNumber - left.offersNumber);

                const targetProduct = targetProductType ? mappedProducts.find(product => product.id === targetProductId && product.type === targetProductType) : mappedProducts.find(product => product.id === targetProductId);

                if (!targetProduct) return null;

                const targetKey = getUniqueKey(targetProduct);
                return {
                    ...targetProduct,
                    rankingByPrice: targetProduct.avgPrice !== null ? rankingByAvgPriceMap[targetKey] : null,
                    rankingByNumberOfOffers: targetProduct.offersNumber > 0 ? rankingByOffersMap[targetKey] : null,
                };
            },
            /** Spłaszcza ceny z serii */
            extractPrices(priceSeries) {
                const collected = [];
                for (const pricesForDay of priceSeries) {
                    for (const [fieldName, value] of Object.entries(pricesForDay)) {
                        if (fieldName !== 'date' && value != null) collected.push(value);
                    }
                }
                return collected;
            },
            /** Wyliczenia cen: średnia, min/max oraz „range” bez skrajnych outlierów (jeśli > 2 wartości) */
            calculatePriceData(productData) {
                if (!productData?.price) {
                    return {
                        avgPrice: null,
                        minPrice: null,
                        maxPrice: null,
                        priceRangeMin: null,
                        priceRangeMax: null,
                    };
                }
                const prices = this.extractPrices(productData.price);
                if (!prices.length) {
                    return {
                        avgPrice: null,
                        minPrice: null,
                        maxPrice: null,
                        priceRangeMin: null,
                        priceRangeMax: null,
                    };
                }

                let sum = 0;
                let minPrice = Infinity;
                let maxPrice = -Infinity;
                let secondMin = Infinity;
                let secondMax = -Infinity;
                const distinctPrices = new Set();

                for (const price of prices) {
                    sum += price;

                    if (price < minPrice) {
                        secondMin = minPrice;
                        minPrice = price;
                    } else if (price > minPrice && price < secondMin) {
                        secondMin = price;
                    }

                    if (price > maxPrice) {
                        secondMax = maxPrice;
                        maxPrice = price;
                    } else if (price < maxPrice && price > secondMax) {
                        secondMax = price;
                    }

                    distinctPrices.add(price);
                }

                const avgPrice = sum / prices.length;

                let priceRangeMin = minPrice;
                let priceRangeMax = maxPrice;
                if (distinctPrices.size > 2) {
                    priceRangeMin = Number.isFinite(secondMin) ? secondMin : minPrice;
                    priceRangeMax = Number.isFinite(secondMax) ? secondMax : maxPrice;
                }
                return { avgPrice, minPrice, maxPrice, priceRangeMin, priceRangeMax };
            },
            /** Liczba aktywnych ofert w końcówce zakresu */
            calculateOffersNumber(productData) {
                if (!productData?.availability) return 0;

                let offersCount = 0;
                const lastRangeTimestamp = this.endMs;

                for (const offersForCampaign of Object.values(productData.availability)) {
                    for (const offerRecord of offersForCampaign) {
                        const segments = offerRecord?.chart?.segments;
                        if (!Array.isArray(segments)) continue;
                        for (const segment of segments) {
                            if (!lastRangeTimestamp || (segment.start <= lastRangeTimestamp && segment.end >= lastRangeTimestamp)) {
                                offersCount++;
                            }
                        }
                    }
                }
                return offersCount;
            },
            /** Tworzy mapę rankingów w postaci "i/total" */
            createRankingMap(items, comparator) {
                const sorted = [...items].sort(comparator);
                const total = sorted.length;
                return sorted.reduce((rankingMap, product, index) => {
                    rankingMap[getUniqueKey(product)] = `${index + 1} / ${total}`; // eslint-disable-line no-param-reassign
                    return rankingMap;
                }, {});
            },
            /** Scala zachodzące na siebie segmenty [start,end] */
            mergeSegments(segments) {
                if (!Array.isArray(segments) || segments.length === 0) return [];

                const normalized = segments
                    .map(segment => ({ start: Number(segment?.start), end: Number(segment?.end) }))
                    .filter(segment => Number.isFinite(segment.start) && Number.isFinite(segment.end) && segment.start <= segment.end)
                    .sort((a, b) => a.start - b.start);

                const merged = [];
                for (const segment of normalized) {
                    if (!merged.length) {
                        merged.push({ ...segment });
                        continue;
                    }
                    const last = merged[merged.length - 1];
                    if (segment.start <= last.end + 1) {
                        if (segment.end > last.end) last.end = segment.end;
                    } else {
                        merged.push({ ...segment });
                    }
                }
                return merged;
            },
            /**
             * ZAWSZE zwraca klucze per sklep po NAZWIE (np. "Avans.pl"):
             * - najpierw bierzemy offer.campaign (to już jest nazwa),
             * - jeśli jej nie ma, bierzemy klucz z serii cen (resolvePriceKeyForCampaign),
             * - ostateczny awaryjny fallback: ID kampanii (nie powinno już być potrzebne).
             */
            buildAvailabilityRanges(productData) {
                const perShopRaw = {};
                const accumulatedRaw = [];

                const availability = productData?.availability || {};
                const priceSeries = productData?.price || [];

                for (const [campaignIdRaw, offersArray] of Object.entries(availability)) {
                    if (!Array.isArray(offersArray)) continue;
                    const campaignId = String(campaignIdRaw);

                    for (const offerRecord of offersArray) {
                        const offerObj = offerRecord?.offer || {};
                        const rawSegments = offerRecord?.chart?.segments || [];
                        if (!Array.isArray(rawSegments) || rawSegments.length === 0) continue;

                        const priceKey = this.resolvePriceKeyForCampaign(campaignId, priceSeries, offerObj);
                        const shopLabel = offerObj.campaign ?? priceKey ?? campaignId; // nazwa sklepu → klucz

                        for (const rawSeg of rawSegments) {
                            const start = Number(rawSeg?.start);
                            const end = Number(rawSeg?.end);
                            if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
                            (perShopRaw[shopLabel] ??= []).push({ start, end });
                            accumulatedRaw.push({ start, end });
                        }
                    }
                }

                const perShop = {};
                for (const [shopLabel, shopSegments] of Object.entries(perShopRaw)) {
                    perShop[shopLabel] = this.mergeSegments(shopSegments);
                }
                const accumulated = this.mergeSegments(accumulatedRaw);
                return { perShop, accumulated };
            },
            /**
             * Zwraca NAZWĘ klucza w serii cen dla danej kampanii/oferty:
             * - najpierw dokładnie `offer.campaign` (np. "Avans.pl"),
             * - w przeciwnym razie wybiera najczęstszy klucz z price[] (bez "date").
             */
            resolvePriceKeyForCampaign(campaignId, priceArray, offerObj) {
                if (offerObj?.campaign) return offerObj.campaign;

                if (!Array.isArray(priceArray) || priceArray.length === 0) return null;

                const counters = Object.create(null);
                for (const dayRecord of priceArray) {
                    for (const [keyName, value] of Object.entries(dayRecord)) {
                        if (keyName === 'date') continue;
                        if (value != null) counters[keyName] = (counters[keyName] || 0) + 1;
                    }
                }
                let bestKey = null;
                let bestCount = -1;
                for (const [keyName, count] of Object.entries(counters)) {
                    if (count > bestCount) {
                        bestCount = count;
                        bestKey = keyName;
                    }
                }
                return bestKey; // np. "Avans.pl", "mediaexpert.pl" itd.
            },
            /** Cena sklepu (po kluczu będącym nazwą) z najbliższego dnia ≤ targetMs */
            getShopPriceAtOrBeforeTimestamp(priceArray, priceKey, targetMs) {
                if (!Array.isArray(priceArray) || !priceKey || !Number.isFinite(targetMs)) return null;

                let bestMatchMs = -Infinity;
                let bestPrice = null;

                for (const dayObject of priceArray) {
                    const dateStr = dayObject?.date;
                    if (!dateStr) continue;
                    const dayMs = dateStringToUtcMs(dateStr);
                    if (dayMs <= targetMs) {
                        const candidate = dayObject[priceKey];
                        if (candidate != null && dayMs > bestMatchMs) {
                            bestMatchMs = dayMs;
                            bestPrice = candidate;
                        }
                    }
                }
                return bestPrice;
            },
            /**
             * Zwraca listy allOffers/activeOffers — metadane wyłącznie z offer.*,
             * bez żadnych „domain/slug/homepage/logo”.
             */
            buildOffersForProduct(productData, timeRangeEndMs) {
                const allOffers = [];
                const activeOffers = [];

                const availability = productData?.availability || {};
                const priceSeries = productData?.price || [];

                for (const [campaignIdRaw, offersArray] of Object.entries(availability)) {
                    if (!Array.isArray(offersArray)) continue;
                    const campaignId = String(campaignIdRaw);

                    for (const rawOffer of offersArray) {
                        const offerObj = rawOffer?.offer || {};
                        const rawSegments = rawOffer?.chart?.segments || [];
                        const mergedSegments = this.mergeSegments(rawSegments);

                        const offerId = offerObj.id ?? null;
                        const offerName = offerObj.name ?? null;
                        const offerUrl = offerObj.url ?? null;

                        const offerCampaignId = String(offerObj.campaignId ?? campaignId);
                        const offerCampaignName = offerObj.campaign ?? null; // nazwa sklepu
                        const offerCampaignUrl = offerObj.campaignUrl ?? null;
                        const offerCampaignIcon = offerObj.campaignIcon ?? null;

                        const priceKey = this.resolvePriceKeyForCampaign(campaignId, priceSeries, offerObj);

                        const baseOffer = {
                            id: offerId,
                            name: offerName,
                            url: offerUrl,
                            campaignId: offerCampaignId,
                            campaign: offerCampaignName ?? priceKey ?? offerCampaignId, // zawsze czytelna etykieta
                            campaignUrl: offerCampaignUrl,
                            campaignIcon: offerCampaignIcon,
                            segments: mergedSegments,
                        };

                        allOffers.push(baseOffer);

                        if (!Number.isFinite(timeRangeEndMs)) continue;

                        const isActiveNow = mergedSegments.some(segment => segment.start <= timeRangeEndMs && segment.end >= timeRangeEndMs);
                        if (!isActiveNow) continue;

                        const priceFromSeries = this.getShopPriceAtOrBeforeTimestamp(priceSeries, priceKey, timeRangeEndMs);
                        const finalPrice = priceFromSeries ?? offerObj.price ?? null;
                        const finalDate = Number.isFinite(timeRangeEndMs) ? timeRangeEndMs : offerObj.date ?? null;

                        activeOffers.push({
                            ...baseOffer,
                            price: finalPrice,
                            date: finalDate,
                        });
                    }
                }

                return { allOffers, activeOffers };
            },
            /** Grupuje ciągłe daty (YYYY-MM-DD) do zakresów [startMs,endMs] w CZASIE LOKALNYM */
            groupDatesToMsRanges(dateStrings) {
                if (!dateStrings?.length) return [];
                const sortedUnique = [...new Set(dateStrings)].sort();

                const ranges = [];
                let rangeStartStr = sortedUnique[0];
                let previousUtcDay = toUtcDay(sortedUnique[0]);

                const pushRange = (startStr, endStr) => {
                    const start = new Date(`${startStr}T00:00:00`).getTime();
                    const end = new Date(`${endStr}T23:59:59.999`).getTime();
                    ranges.push({ start, end });
                };

                for (let i = 1; i < sortedUnique.length; i++) {
                    const currentUtcDay = toUtcDay(sortedUnique[i]);
                    if (currentUtcDay === previousUtcDay + 1) {
                        previousUtcDay = currentUtcDay;
                    } else {
                        const endIso = new Date(previousUtcDay * 86400000).toISOString().split('T')[0];
                        pushRange(rangeStartStr, endIso);
                        rangeStartStr = sortedUnique[i];
                        previousUtcDay = currentUtcDay;
                    }
                }
                const lastEndIso = new Date(previousUtcDay * 86400000).toISOString().split('T')[0];
                pushRange(rangeStartStr, lastEndIso);
                return ranges;
            },
        },
        created() {
            this.fetchReportSet();
        },
    };
</script>
